import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Company } from '@/types/settings';
import type { TenantBasicInfoApi } from '@/types/api/user-context';

interface CompanyContextType {
  selectedCompany: Company | null;
  companies: Company[];
  setSelectedCompany: (company: Company) => void;
  isLoading: boolean;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

// Mock companies data - used when running in 'app' host or demo mode
const mockCompanies: Company[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    code: 'ACME',
    currency: 'USD',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-001',
    registrationDate: '2024-01-15',
    status: 'active',
    createdAt: '2024-01-15T10:00:00Z',
    organizationId: 'org-1',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    code: 'GLOB',
    currency: 'EUR',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-002',
    registrationDate: '2024-02-20',
    status: 'active',
    createdAt: '2024-02-20T14:30:00Z',
    organizationId: 'org-1',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    code: 'TECH',
    currency: 'USD',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-003',
    registrationDate: '2024-03-10',
    status: 'active',
    createdAt: '2024-03-10T09:15:00Z',
    organizationId: 'org-1',
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    code: 'RETL',
    currency: 'GBP',
    fiscalYearStart: '2024-01-01',
    fiscalYearEnd: '2024-12-31',
    taxId: 'TAX-004',
    registrationDate: '2024-01-05',
    status: 'active',
    createdAt: '2024-01-05T11:45:00Z',
    organizationId: 'org-1',
  },
];

const STORAGE_KEY = 'selectedCompanyId';

/**
 * Convert TenantBasicInfoApi from userContext to Company type
 * Only id and name are available from userContext, other fields use defaults
 */
function convertTenantToCompany(tenant: TenantBasicInfoApi): Company {
  return {
    id: String(tenant.id),
    name: tenant.name,
    code: '',
    currency: '',
    fiscalYearStart: '',
    fiscalYearEnd: '',
    taxId: '',
    registrationDate: '',
    status: 'active',
    createdAt: '',
    organizationId: '',
  };
}

export interface CompanyProviderProps {
  children: ReactNode;
  /** External tenants from userContext (used when host is 'app-prod') */
  externalTenants?: TenantBasicInfoApi[];
  /** Whether external tenants are still loading */
  externalTenantsLoading?: boolean;
}

export function CompanyProvider({ 
  children, 
  externalTenants,
  externalTenantsLoading = false,
}: CompanyProviderProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompanyState] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine if we should use external tenants (from userContext in app-prod)
  const useExternalTenants = externalTenants !== undefined;

  // Load companies and restore selected company from localStorage
  useEffect(() => {
    const loadCompanies = async () => {
      setIsLoading(true);
      try {
        let activeCompanies: Company[];

        if (useExternalTenants) {
          // Use external tenants from userContext (app-prod mode)
          if (externalTenantsLoading) {
            // Still loading, wait for next effect trigger
            return;
          }
          activeCompanies = (externalTenants || []).map(convertTenantToCompany);
        } else {
          // Use mock data (app mode or standalone)
          await new Promise(resolve => setTimeout(resolve, 300));
          activeCompanies = mockCompanies.filter(c => c.status === 'active');
        }

        setCompanies(activeCompanies);

        // Restore selected company from localStorage
        const savedCompanyId = localStorage.getItem(STORAGE_KEY);
        if (savedCompanyId) {
          const savedCompany = activeCompanies.find(c => c.id === savedCompanyId);
          if (savedCompany) {
            setSelectedCompanyState(savedCompany);
          } else {
            // If saved company not found, select first company
            setSelectedCompanyState(activeCompanies[0] || null);
          }
        } else {
          // No saved company, select first one
          setSelectedCompanyState(activeCompanies[0] || null);
        }
      } catch (error) {
        console.error('Failed to load companies:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanies();
  }, [useExternalTenants, externalTenants, externalTenantsLoading]);

  const setSelectedCompany = (company: Company) => {
    setSelectedCompanyState(company);
    localStorage.setItem(STORAGE_KEY, company.id);
  };

  // Show loading state if external tenants are still loading
  const effectiveIsLoading = isLoading || (useExternalTenants && externalTenantsLoading);

  return (
    <CompanyContext.Provider
      value={{
        selectedCompany,
        companies,
        setSelectedCompany,
        isLoading: effectiveIsLoading,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
